import { alpha, decomposeColor, hslToRgb, rgbToHex } from '@mui/material/styles';
import type { ColorSystemOptions } from '@mui/material/styles';
import type { PresetColors } from './presets';
import type { ZuulColorMode } from './useZuulColorMode';

/** Hue (0-360) and saturation (0-1) of a color, used to tint the neutrals toward the preset. */
interface Tone {
  hue: number;
  saturation: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** MUI ships hsl → rgb but not the reverse, and the presets are authored as hex. */
function toneOf(color: string): Tone {
  const [r, g, b] = decomposeColor(color).values.map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  if (max === min) return { hue: 0, saturation: 0 };
  const delta = max - min;
  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  let hue: number;
  if (max === r) hue = ((g - b) / delta) % 6;
  else if (max === g) hue = (b - r) / delta + 2;
  else hue = (r - g) / delta + 4;
  return { hue: (hue * 60 + 360) % 360, saturation };
}

/** How far a neutral is pulled toward the preset hue, and where it sits on the lightness axis. */
interface Tint {
  /** Multiplier on the source saturation — vivid presets tint more than already-muted ones. */
  strength: number;
  /** Saturation floor, so a low-saturation preset still reads as tinted rather than pure grey. */
  min: number;
  /** Saturation ceiling, so a vivid preset stays a muted neutral rather than a colored panel. */
  max: number;
  lightness: number;
}

/**
 * A neutral carrying a trace of the preset's hue. An achromatic preset (the graphite default)
 * skips the saturation floor: clamping a zero-saturation source up to `min` would paint every
 * surface with hue 0 — pink — instead of the greys the preset asks for.
 */
function tinted({ hue, saturation }: Tone, { strength, min, max, lightness }: Tint): string {
  const s = saturation < 0.05 ? 0 : clamp(saturation * strength, min, max);
  return rgbToHex(hslToRgb(`hsl(${Math.round(hue)}, ${(s * 100).toFixed(1)}%, ${(lightness * 100).toFixed(1)}%)`));
}

/**
 * Where each neutral sits per scheme. Backgrounds carry the most hue (that is the "muted version
 * of the palette color" the surfaces are painted in); text carries the least, so contrast against
 * the background stays the dominant signal.
 *
 * The surfaces form four tiers so a busy screen zones itself: `default` is the page wash, `paper`
 * the card bodies, `shell` the nav chrome, with `sectionHeader` banding the top of
 * cards/tables/dialogs and `inset` for wells nested inside a card. The zoning test in
 * presets.test.tsx guards the tiers staying visibly apart in both schemes.
 *
 * `shell` sits ABOVE the page wash in both schemes — the frame is lighter than what it frames.
 * It was briefly the deepest tint instead, which made the sidebar a saturated band in light mode
 * and a near-black slab in dark; the console has always read as chrome-over-wash, and the divider
 * along the sidebar's edge is what separates the two, not a step down in lightness.
 */
const TINTS: Record<
  ZuulColorMode,
  Record<
    | 'default'
    | 'paper'
    | 'sectionHeader'
    | 'inset'
    | 'shell'
    | 'divider'
    | 'textPrimary'
    | 'textSecondary'
    | 'textDisabled',
    Tint
  >
> = {
  light: {
    default: { strength: 1.1, min: 0.34, max: 0.74, lightness: 0.878 },
    paper: { strength: 0.9, min: 0.28, max: 0.6, lightness: 0.963 },
    sectionHeader: { strength: 1.0, min: 0.3, max: 0.7, lightness: 0.93 },
    inset: { strength: 0.8, min: 0.24, max: 0.5, lightness: 0.94 },
    // Near-white, and fractionally lighter than `paper`: the sidebar and header are the calm
    // edge of the screen, so they take the least hue of any surface.
    shell: { strength: 0.75, min: 0.22, max: 0.48, lightness: 0.972 },
    divider: { strength: 0.8, min: 0.24, max: 0.5, lightness: 0.76 },
    textPrimary: { strength: 0.35, min: 0.1, max: 0.3, lightness: 0.14 },
    textSecondary: { strength: 0.28, min: 0.08, max: 0.24, lightness: 0.34 },
    textDisabled: { strength: 0.22, min: 0.06, max: 0.18, lightness: 0.56 },
  },
  dark: {
    // Below roughly 10% lightness every hue collapses to the same near-black, which is why the
    // dark renditions used to be indistinguishable. These sit high enough to carry visible color.
    // The saturation ceiling is a balance, not a preference: much under 0.40 and the closest pair
    // (Emerald at hue 142, Teal at 174) converges again — the separation test is the guard.
    // `shell` sits between the wash and the card bodies: above the 10% floor, so the nav chrome
    // reads as the preset's hue rather than as black, which is what made it feel cut out of the
    // page rather than part of it.
    default: { strength: 0.9, min: 0.21, max: 0.52, lightness: 0.115 },
    paper: { strength: 0.77, min: 0.18, max: 0.42, lightness: 0.17 },
    sectionHeader: { strength: 0.8, min: 0.19, max: 0.46, lightness: 0.215 },
    inset: { strength: 0.75, min: 0.15, max: 0.35, lightness: 0.14 },
    shell: { strength: 0.8, min: 0.19, max: 0.44, lightness: 0.148 },
    divider: { strength: 0.55, min: 0.13, max: 0.29, lightness: 0.33 },
    textPrimary: { strength: 0.3, min: 0.06, max: 0.2, lightness: 0.93 },
    textSecondary: { strength: 0.24, min: 0.05, max: 0.16, lightness: 0.72 },
    textDisabled: { strength: 0.2, min: 0.04, max: 0.14, lightness: 0.5 },
  },
};
/**
 * Interaction states, as translucent primary so hover and selection read as part of the preset.
 * The opacities are set alongside the colors because components are split on which they use —
 * MenuItem, for one, composes `alpha(primary.main, selectedOpacity + hoverOpacity)` for a hovered
 * selected row, so a mismatch shows up as a hover that jumps to a different tint.
 */
const ACTIONS: Record<ZuulColorMode, { active: number; hover: number; selected: number; focus: number }> = {
  light: { active: 0.62, hover: 0.06, selected: 0.11, focus: 0.12 },
  dark: { active: 0.7, hover: 0.1, selected: 0.18, focus: 0.18 },
};

/**
 * Status colors are meaning, not branding — they stay put while the preset changes around them.
 * A preset in the same hue family as one of these (Emerald next to success, say) is unavoidable
 * with eight brand hues on one wheel; alerts and helper text carry icons to disambiguate.
 */
const STATUS = {
  light: {
    success: { main: '#16a34a' },
    info: { main: '#0891b2' },
    warning: { main: '#d97706' },
    error: { main: '#d11f1f' }, // dark enough to stay legible as text on the tinted paper
  },
  dark: {
    success: { main: '#4ade80' },
    info: { main: '#22d3ee' },
    warning: { main: '#fbbf24' },
    error: { main: '#f87171' },
  },
} as const;

/** Zoning surfaces beyond MUI's two-background model; consumed as `palette.zuul.*`. */
export interface ZuulSurfaces {
  /** Nav chrome (sidebar, app header): lighter than the page wash, so the frame reads as an edge. */
  shell: string;
  /** Band across the top of cards, tables and dialogs — the hue-carrying section marker. */
  sectionHeader: string;
  /** Wells nested inside a card body (footers, table heads, empty states). */
  inset: string;
  /** Accent for the Zuul logo's strokes; the preset's `logoAccent`, falling back to secondary. */
  logoAccent: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    zuul: ZuulSurfaces;
  }
  interface PaletteOptions {
    zuul?: ZuulSurfaces;
  }
}

/**
 * Builds a complete palette from a preset's primary/secondary pair. Backgrounds, dividers, text
 * and interaction states are all derived from the primary's hue as muted neutrals, so picking a
 * preset re-colors every MUI component rather than only the ones that use `color="primary"`.
 */
/**
 * The palette of one color scheme: `PaletteOptions` plus the per-component colors (TableCell,
 * Switch, ...) that a CSS-variables theme accepts under `colorSchemes.<scheme>.palette`.
 */
export type SchemePaletteOptions = NonNullable<ColorSystemOptions['palette']>;

export function derivePalette(colors: PresetColors, mode: ZuulColorMode): SchemePaletteOptions {
  const tone = toneOf(colors.primary);
  const tint = TINTS[mode];
  const action = ACTIONS[mode];
  const divider = tinted(tone, tint.divider);
  return {
    mode,
    primary: { main: colors.primary },
    secondary: { main: colors.secondary },
    ...STATUS[mode],
    background: {
      default: tinted(tone, tint.default),
      paper: tinted(tone, tint.paper),
    },
    zuul: {
      shell: tinted(tone, tint.shell),
      sectionHeader: tinted(tone, tint.sectionHeader),
      inset: tinted(tone, tint.inset),
      logoAccent: colors.logoAccent ?? colors.secondary,
    },
    text: {
      primary: tinted(tone, tint.textPrimary),
      secondary: tinted(tone, tint.textSecondary),
      disabled: tinted(tone, tint.textDisabled),
    },
    divider,
    // MUI derives a table cell's border from the divider by lightening it 88% (light) or darkening
    // it 68% (dark), on the assumption that the divider is its translucent black. Ours is already
    // an opaque neutral, so the lightened result vanished against paper. Rows take the divider as
    // is, the same rule card headers and dialog titles draw.
    TableCell: { border: divider },
    action: {
      active: alpha(colors.primary, action.active),
      hover: alpha(colors.primary, action.hover),
      hoverOpacity: action.hover,
      selected: alpha(colors.primary, action.selected),
      selectedOpacity: action.selected,
      focus: alpha(colors.primary, action.focus),
      focusOpacity: action.focus,
    },
  };
}
